/**
 * Tool UI for rendering query results as a styled data table.
 * Registered specifically for `run_custom_query`.
 *
 * A query result reaches this component in one of several shapes, because two
 * independent offload layers sit between the BigQuery client and the browser:
 *
 *   1. inline           — `{ rowCount, rows }`, when the JSON fits the tool
 *                         envelope's inline limit (4KB by default).
 *   2. pointer envelope — `{ _dataRef, sizeBytes, head, tail, summary }`, when
 *                         it does not. The rows live in a workspace file. This
 *                         is the COMMON case: 4KB is roughly 25 rows.
 *   3. blob ref         — `{ _blobRef, preview, totalBytes }`, applied later by
 *                         persist-time compaction on reload of an old thread.
 *   4. truncated        — `{ _truncated, head, tail }`, when offloading failed.
 *
 * Only shape 1 carries `rows`, so anything that keys off `result.rows` alone
 * reports a successful query as empty. Each shape is handled below.
 */
import { useState } from 'react';
import { makeAssistantToolUI } from '@assistant-ui/react';
import { Table, Database, AlertCircle, Download } from 'lucide-react';
import { ToolRow, RowAction, DetailPanel, DetailPre, plainText } from '../toolRow';
import { BlobRefView, extractBlobRef } from '../BlobRefView';
import { useOffloadedResult, fmtBytes, type EnvelopeFields } from './useOffloadedResult';

interface TableResult extends EnvelopeFields {
  rowCount?: number;
  truncatedTo?: number;
  rows?: Record<string, unknown>[];
  error?: string;

  // Free pre-flight scan estimate
  dryRun?: boolean;
  bytesProcessed?: number;
  gbProcessed?: number;
  capGb?: number;
  ceilingGb?: number;
  wouldExceedCap?: boolean;
  note?: string;
}

function TableShell({
  rowCount,
  truncatedTo,
  rows,
  note,
}: {
  rowCount?: number;
  truncatedTo?: number;
  rows: Record<string, unknown>[];
  note?: string;
}) {
  const [open, setOpen] = useState(false);
  const columns = Object.keys(rows[0] ?? {});
  const displayRows = rows.slice(0, 20);
  const total = rowCount ?? rows.length;

  return (
    <ToolRow
      icon={<Table />}
      label={`${total} row${total !== 1 ? 's' : ''}${truncatedTo ? ` (first ${truncatedTo})` : ''}`}
      note={note}
      meta={columns.length > 0 ? <span className="inline-block max-w-[240px] truncate align-bottom">{columns.join(', ')}</span> : undefined}
      open={open}
      onToggle={() => setOpen((v) => !v)}
    >
      <div className="overflow-hidden rounded-md border border-zinc-200 bg-white">
        <div className="max-h-[300px] overflow-auto">
          <table className="w-full text-meta">
            <thead className="sticky top-0 bg-white">
              <tr>
                {columns.map((col) => (
                  <th key={col} className="whitespace-nowrap border-b border-zinc-200 px-3 py-1.5 text-left font-medium text-zinc-500">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row: Record<string, unknown>, i: number) => (
                <tr key={i} className="border-t border-zinc-100 first:border-t-0">
                  {columns.map((col) => (
                    <td key={col} className="max-w-[200px] truncate whitespace-nowrap px-3 py-1.5 text-zinc-800 tabular-nums">
                      {formatValue(row[col])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {displayRows.length < rows.length && (
          <div className="border-t border-zinc-200 px-3 py-1.5 text-xs text-zinc-400">
            Showing {displayRows.length} of {rows.length} rows
          </div>
        )}
      </div>
    </ToolRow>
  );
}

/**
 * Rows that were offloaded to a workspace file. Fetches `.data/<ref>.json`
 * through the workspace preview endpoint and renders the real table, so a
 * large successful query looks like a large successful query.
 */
function DataRefTable({ result }: { result: TableResult }) {
  const { data, loading, error, load, sizeBytes, summary } = useOffloadedResult<TableResult>(result);
  const [open, setOpen] = useState(false);

  if (data && Array.isArray(data.rows)) {
    return (
      <TableShell
        rows={data.rows}
        rowCount={data.rowCount}
        truncatedTo={data.truncatedTo}
        note={fmtBytes(sizeBytes)}
      />
    );
  }

  return (
    <ToolRow
      icon={error ? <AlertCircle className="text-red-500" /> : <Database />}
      label={loading ? 'Loading results…' : summary || 'Query results'}
      running={loading}
      tone={error ? 'error' : 'default'}
      meta={error ? <span className="text-red-600">{error}</span> : sizeBytes > 0 ? fmtBytes(sizeBytes) : undefined}
      open={open}
      onToggle={result.head ? () => setOpen((v) => !v) : undefined}
      actions={!loading ? (
        <RowAction title={error ? 'Retry loading the table' : 'Show table'} onClick={load}>
          <Download className="w-3 h-3" />
          {error ? 'Retry' : 'Show table'}
        </RowAction>
      ) : undefined}
    >
      {result.head ? (
        <DetailPanel label="Preview">
          <DetailPre maxH="max-h-[160px]">{result.head}…</DetailPre>
        </DetailPanel>
      ) : null}
    </ToolRow>
  );
}

export const DataTableToolUI = makeAssistantToolUI<Record<string, unknown>, TableResult>({
  toolName: 'run_custom_query',
  render: ({ args, result, status }) => {
    if (status?.type === 'running' || !result) {
      const thought = (args as any)?._thought as string | undefined;
      const query = (args as any)?.query as string | undefined;
      const label = query ? `Running ${query.replace(/\s+/g, ' ').slice(0, 80)}${query.length > 80 ? '…' : ''}` : 'Running BigQuery query';
      return <ToolRow icon={<Database />} label={plainText(thought) || label} title={query} running open={false} />;
    }

    if (result.error) {
      return <ToolRow icon={<AlertCircle className="text-red-500" />} label={result.error} tone="error" open={false} />;
    }

    // Free pre-flight scan estimate — a successful call that never has rows.
    if (result.dryRun) {
      const over = result.wouldExceedCap;
      return (
        <ToolRow
          icon={over ? <AlertCircle className="text-amber-500" /> : <Database />}
          label={`Dry run · would scan ${result.gbProcessed ?? 0} GB${result.capGb != null ? ` of a ${result.capGb} GB cap` : ''}`}
          note={result.note}
          meta={over ? <span className="text-amber-600">over cap</span> : undefined}
          tone={over ? 'warn' : 'default'}
          open={false}
        />
      );
    }

    // Persist-time compaction replaced the payload with a blob pointer.
    const blobRef = extractBlobRef(result);
    if (blobRef) {
      return <div className="my-1"><BlobRefView ref={blobRef} /></div>;
    }

    // Tool-time offload — the rows are in a workspace file.
    if (result._dataRef) {
      return <DataRefTable result={result} />;
    }

    // Offload was attempted and failed; only head/tail survived.
    if (result._truncated) {
      return <TruncatedResult result={result} />;
    }

    if (!result.rows || result.rows.length === 0) {
      return <ToolRow icon={<Table />} label="No results returned" open={false} />;
    }

    return (
      <TableShell
        rows={result.rows}
        rowCount={result.rowCount}
        truncatedTo={result.truncatedTo}
      />
    );
  },
});

function TruncatedResult({ result }: { result: TableResult }) {
  const [open, setOpen] = useState(false);
  return (
    <ToolRow
      icon={<AlertCircle className="text-amber-500" />}
      label="Results too large to display"
      note={result._reason}
      meta={result.sizeBytes ? fmtBytes(result.sizeBytes) : undefined}
      tone="warn"
      open={open}
      onToggle={result.head ? () => setOpen((v) => !v) : undefined}
    >
      {result.head ? (
        <DetailPanel label="Preview">
          <DetailPre maxH="max-h-[160px]">{result.head}…</DetailPre>
        </DetailPanel>
      ) : null}
    </ToolRow>
  );
}

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'number') {
    return Number.isInteger(val) ? val.toLocaleString() : val.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'object') { try { return JSON.stringify(val); } catch { return String(val); } }
  return String(val);
}
